import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDay, formatDisplayDate, formatDisplayTime } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { formatWireForDisplay } from '../money.js'
import { sentence } from '../sentence.js'
import {
  movementKindLabel,
  ownershipLabel,
  stockFlagLabel,
  stockReasonLabel,
} from '../status-labels.js'
import type { StockLocationOption } from './Purchasing.js'
import { StockWrites } from './StockWrites.js'

/**
 * What is on the shelf. §6.10 `INV-001` to `INV-009`.
 *
 * **A read-only screen on purpose, for now.** The fifteen stock routes split into three jobs a
 * shopkeeper would recognise: what is here, receiving and adjusting, and counting. This is the
 * first, and it is the one every role can do: `stock_view` is the only Appendix B stock row every
 * role holds. Receiving and adjusting are `stock_receive` and `stock_adjust`, and counting is
 * blocked outright, because `stock_adjust` is owner and manager only while a stocktake is a thing a
 * dispenser does with a clipboard. The stock controller says that is a gap in the matrix rather
 * than a decision, so it is `later_todo.md` rather than a screen that refuses.
 *
 * **A location is chosen, never assumed.** `INV-001` makes stock a branch's, and `BR-10` makes a
 * non-selling shelf real stock the counter cannot sell, so which shelf is being looked at changes
 * what the numbers mean. The list comes from `GET /stock/locations`, which exists because nothing
 * produced a location id until today.
 *
 * **A negative quantity is shown, not hidden.** §12 case 47 is a sale that drove stock below zero,
 * and `negative_stock` is the flag the engine sets for it. A screen that clamped it at zero would
 * hide the one number that says the shelf and the record disagree.
 *
 * **An absent value is Appendix B's cost row withheld** (`ROLE-001a`), so the screen says nothing
 * about value rather than printing a zero, and a consignment line is genuinely zero-valued
 * (`INV-003`), which is a different statement the words keep apart.
 */

export interface StockPositionData {
  readonly stockItemId: string
  readonly ownership: string
  /**
   * What the stock is, in words. Absent when the catalogue row is missing.
   *
   * This screen printed `frameVariantId` before, which is a UUID in a real tenant and reads as a
   * product name only against the development seed, because the seed chooses readable ids.
   */
  readonly label?: string
  readonly lot: string
  readonly quantity: number
  readonly flags: readonly string[]
  readonly currency: string
  readonly expiryDate?: string
  readonly tray?: string
  readonly frameVariantId?: string
  readonly lensProductId?: string
  /** Absent means Appendix B's cost row is withheld from this reader. */
  readonly valueMinor?: string
  readonly version: number
}

export interface StockMovementData {
  readonly id: string
  readonly kind: string
  readonly qtyDelta: number
  readonly recordedAt: string
  readonly reason?: string
  readonly refType?: string
  readonly reversesMovementId?: string
  /** Absent means withheld, as everywhere else. */
  readonly unitCostMinor?: string
  readonly currency?: string
}

/** One trial item on the expiry watch. §12 case 118, `REF-010`, `INV-009`. */
export interface TrialExpiryRow {
  readonly stockItemId: string
  readonly lot: string
  readonly quantity: number
  readonly expiryDate: string
  readonly locationName: string
  readonly locationCode: string
}

/**
 * Two lists, because they need different sentences. An expired trial lens must come out of the
 * case today; one expiring next month is something to reorder. Merged, the urgent one is missable.
 */
export interface TrialExpiryData {
  readonly today: string
  readonly withinDays: number
  readonly expired: readonly TrialExpiryRow[]
  readonly expiringSoon: readonly TrialExpiryRow[]
  readonly undatedItems: number
}

/**
 * The windows the panel offers. §12 case 118, `INV-009`.
 *
 * **Zero first, and it is not a default window.** `INV-009` leaves the near-expiry window
 * unspecified and the server refuses a request that does not say, so a figure chosen here would be
 * a policy nobody set. At zero the panel shows what is already expired plus anything expiring
 * today, which needs no window to be true, and the longer views are an explicit choice.
 */
const EXPIRY_WINDOWS = [0, 30, 60, 90] as const

export interface StockProps {
  readonly loadLocations: () => Promise<readonly StockLocationOption[]>
  /**
   * §12 case 118. Absent on an older server, and then the panel is not offered rather than offered
   * empty, which would say a drawer is clear when nothing had been read.
   */
  readonly loadTrialExpiry?: (withinDays: number) => Promise<TrialExpiryData>
  readonly loadPositions: (locationId: string) => Promise<readonly StockPositionData[]>
  readonly loadMovements: (stockItemId: string) => Promise<readonly StockMovementData[]>
  /** The branch's own zone, so a movement's time reads as the shop's clock. */
  readonly timezone: string
  /**
   * `stock_receive` and `stock_adjust`, which are two Appendix B rows and not one.
   *
   * This screen stays readable by every role, because `stock_view` is the only stock row every
   * role holds. The write panel appears under an open position for whichever of the two the reader
   * has, and for neither it does not appear at all: no door beats a door that answers 403.
   */
  readonly canReceive: boolean
  readonly canAdjust: boolean
  /** Called once either write has been taken, so the shelf is re-read rather than patched here. */
  readonly onWritten?: () => void
}

export function Stock({
  loadLocations,
  loadTrialExpiry,
  loadPositions,
  loadMovements,
  timezone,
  canReceive,
  canAdjust,
  onWritten,
}: StockProps): JSX.Element {
  const { t, locale } = useTranslation()
  const [locations, setLocations] = useState<readonly StockLocationOption[]>([])
  const [locationId, setLocationId] = useState('')
  const [positions, setPositions] = useState<readonly StockPositionData[] | null>(null)
  /** Bumped by the surface's retry, so a shelf that failed to read can be asked again. */
  const [attempt, setAttempt] = useState(0)
  const [openItem, setOpenItem] = useState<string | null>(null)
  const [movements, setMovements] = useState<readonly StockMovementData[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expiryWindow, setExpiryWindow] = useState<number>(0)
  const [trialExpiry, setTrialExpiry] = useState<TrialExpiryData | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadLocations()
      .then((loaded) => {
        if (cancelled) return
        setLocations(loaded)
        // The only shelf there is, is not a choice. More than one is.
        if (loaded.length === 1) setLocationId(loaded[0]?.id ?? '')
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : t('stock.loadFailed'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [loadLocations, t])

  /**
   * §12 case 118. Read only when the branch actually has a trial location, because for a shop with
   * no trial case the panel is noise, and most shops will not have one at first.
   */
  const hasTrialLocation = locations.some((l) => l.isTrial)

  useEffect(() => {
    if (loadTrialExpiry === undefined || !hasTrialLocation) {
      setTrialExpiry(null)
      return
    }
    let cancelled = false
    void loadTrialExpiry(expiryWindow)
      .then((loaded) => {
        if (!cancelled) setTrialExpiry(loaded)
      })
      .catch(() => {
        // Left absent rather than reported: this panel is beside the shelf a shopkeeper came for,
        // and a failed read of it must not put an error over the screen's actual job.
        if (!cancelled) setTrialExpiry(null)
      })
    return () => {
      cancelled = true
    }
  }, [expiryWindow, hasTrialLocation, loadTrialExpiry])

  useEffect(() => {
    if (locationId === '') {
      setPositions(null)
      return
    }
    let cancelled = false
    void loadPositions(locationId)
      .then((loaded) => {
        if (!cancelled) {
          setPositions(loaded)
          setError(null)
        }
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setPositions([])
          setError(loadError instanceof Error ? loadError.message : t('stock.loadFailed'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [loadPositions, locationId, t, attempt])

  const openMovements = useCallback(
    async (stockItemId: string) => {
      if (openItem === stockItemId) {
        setOpenItem(null)
        setMovements(null)
        return
      }
      setOpenItem(stockItemId)
      setMovements(null)
      try {
        setMovements(await loadMovements(stockItemId))
        setError(null)
      } catch (loadError) {
        setMovements([])
        setError(loadError instanceof Error ? loadError.message : t('stock.movementsFailed'))
      }
    },
    [loadMovements, openItem, t],
  )

  const when = (iso: string): string => {
    const instant = Date.parse(iso)
    if (!Number.isFinite(instant)) return iso
    // The catalogue's own formatters, never `Intl` here: `LOC-004`, `D-03` and `TY-03` keep the
    // counter on Western digits in every locale, and `packages/i18n/src/format.ts` is the one place
    // that decides what a date looks like.
    return `${formatDisplayDate(instant, timezone, locale)} ${formatDisplayTime(instant, timezone, locale)}`
  }

  /** What a line is of. `INV-001` keys stock by catalogue variant, and ids are what the read has. */
  // The words, never the id. An item the catalogue does not hold says so: `stock.unknownItem` is
  // "Item not in the catalogue", which is a fact a shopkeeper can act on, and a UUID is not.
  const whatItIs = (position: StockPositionData): string =>
    position.label ?? t('stock.unknownItem')

  return (
    <section className="lm-screen" aria-labelledby="stock-heading">
      <h1 id="stock-heading" className="lm-screen__heading">
        {t('stock.heading')}
      </h1>

      {locations.length === 0 ? (
        <p className="lm-empty">{t('stock.noLocations')}</p>
      ) : (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('stock.locationLabel')}</span>
          <select
            className="lm-input"
            value={locationId}
            onChange={(e) => {
              setLocationId(e.currentTarget.value)
              setOpenItem(null)
              setMovements(null)
            }}
          >
            <option value="">{t('stock.chooseLocation')}</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {/* `BR-10` and `BR-04` travel with the name: what is on a back store shelf is real
                    stock the counter cannot sell, and the central warehouse is neither. */}
                {location.isCentralWarehouse
                  ? t('stock.locationWarehouse', { name: location.name })
                  : location.isSelling
                    ? location.name
                    : t('stock.locationNotSelling', { name: location.name })}
              </option>
            ))}
          </select>
        </label>
      )}

      {/*
        §8 `ST-02`. The error is inside the surface now rather than a hint above it, so it carries
        the retry the requirement asks for: before this, a shelf that failed to read left a line of
        grey text and no way out but reloading the app, which on a counter tablet loses whatever
        was in hand.
      */}
      {locationId === '' ? null : (
        <DataSurface
          state={
            error !== null
              ? { kind: 'error', message: error, onRetry: () => setAttempt((a) => a + 1) }
              : positions === null
                ? { kind: 'loading' }
                : positions.length === 0
                  ? { kind: 'empty' }
                  : { kind: 'ready' }
          }
          shape="rows"
          rows={4}
          what={t('stock.what')}
          emptyMessage={t('stock.noneHere')}
        >
        <ul className="lm-stock-list">
          {(positions ?? []).map((position) => (
            <li key={position.stockItemId} className="lm-stock">
              <span className="lm-stock__what">{whatItIs(position)}</span>
              <span className="lm-stock__ownership">{ownershipLabel(t, position.ownership)}</span>
              <span className="lm-stock__quantity" dir="ltr">
                {t('stock.onHand', { count: position.quantity })}
              </span>
              <span className="lm-stock__lot" dir="ltr">
                <Ltr>{position.lot}</Ltr>
              </span>

              {position.expiryDate === undefined ? null : (
                <span className="lm-stock__expiry">
                  {sentence(t, 'stock.expires', { date: <Ltr>{formatDay(position.expiryDate, timezone, locale)}</Ltr> })}
                </span>
              )}

              {position.tray === undefined ? null : (
                <span className="lm-stock__tray">{t('stock.tray', { tray: position.tray })}</span>
              )}

              {position.flags.map((flag) => (
                // §12 case 47: the shelf and the record disagree, which is a thing to act on and
                // the one number a screen must not quietly round up to zero.
                <span key={flag} className="lm-stock__flag">
                  {stockFlagLabel(t, flag)}
                </span>
              ))}

              {position.valueMinor === undefined ? null : (
                <span className="lm-stock__value" dir="ltr">
                  {t('stock.worth', {
                    amount: formatWireForDisplay(position.valueMinor, position.currency),
                  })}
                </span>
              )}

              <button
                type="button"
                className="lm-button lm-button--secondary"
                aria-expanded={openItem === position.stockItemId}
                onClick={() => void openMovements(position.stockItemId)}
              >
                {t('stock.history')}
              </button>

              {openItem === position.stockItemId ? (
                movements === null ? (
                  <p className="lm-hint">{t('stock.loadingHistory')}</p>
                ) : movements.length === 0 ? (
                  <p className="lm-hint">{t('stock.noHistory')}</p>
                ) : (
                  <ul className="lm-movement-list">
                    {movements.map((movement) => (
                      <li key={movement.id} className="lm-movement">
                        <span className="lm-movement__kind">
                          {movementKindLabel(t, movement.kind)}
                        </span>
                        <span className="lm-movement__delta" dir="ltr">
                          {/* Signed and printed as given: a reversal is a compensating movement
                              (`INV-005`), so the minus is the record and not a presentation. */}
                          {movement.qtyDelta > 0
                            ? `+${String(movement.qtyDelta)}`
                            : String(movement.qtyDelta)}
                        </span>
                        <span className="lm-movement__when" dir="ltr">
                          <Ltr>{when(movement.recordedAt)}</Ltr>
                        </span>
                        {movement.reason === undefined ? null : (
                          // Appendix E's stock adjustment reasons. This printed the code, so the
                          // movement history read `damaged_in_shop` and `cycle_count_correction`
                          // while `StockWrites` offered the same values as worded choices three
                          // screens away. `stockReasonLabel` falls back to the code for a reason
                          // this build does not know (`ARC-020`).
                          <span className="lm-movement__reason">
                            {stockReasonLabel(t, movement.reason)}
                          </span>
                        )}
                        {movement.reversesMovementId === undefined ? null : (
                          <span className="lm-movement__reverses">{t('stock.reverses')}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )
              ) : null}

              {openItem === position.stockItemId && (canReceive || canAdjust) ? (
                <StockWrites
                  // Keyed on the item and its version: a receipt or a correction bumps the row, the
                  // parent re-reads, and the form should come back empty rather than holding
                  // figures that have already been sent.
                  key={`${position.stockItemId}-${String(position.version)}`}
                  position={position}
                  locationId={locationId}
                  canReceive={canReceive}
                  canAdjust={canAdjust}
                  {...(onWritten === undefined ? {} : { onWritten })}
                />
              ) : null}
            </li>
          ))}
        </ul>
        </DataSurface>
      )}

      {/* §12 case 118, `REF-010`, `INV-009`. Offered only where the branch has a trial case, and
          only on a server that answers the read. */}
      {trialExpiry === null ? null : (
        <section className="lm-setup-panel" aria-labelledby="trial-expiry-heading">
          <h2 id="trial-expiry-heading" className="lm-setup-panel__heading">
            {t('trialExpiry.heading')}
          </h2>
          <p className="lm-setup-panel__effects">{t('trialExpiry.means')}</p>

          <label className="lm-field">
            <span className="lm-field__label">{t('trialExpiry.windowLabel')}</span>
            {/* `INV-009` names no window, so zero is first and is not a default: at zero this shows
                what is already expired, which needs no window to be true. */}
            <select
              className="lm-input"
              value={String(expiryWindow)}
              onChange={(e) => setExpiryWindow(Number(e.currentTarget.value))}
            >
              {EXPIRY_WINDOWS.map((days) => (
                <option key={days} value={String(days)}>
                  {days === 0
                    ? t('trialExpiry.windowExpiredOnly')
                    : t('trialExpiry.windowDays', { days })}
                </option>
              ))}
            </select>
          </label>

          <h3 className="lm-field__label">{t('trialExpiry.expiredHeading')}</h3>
          {trialExpiry.expired.length === 0 ? (
            <p className="lm-hint">{t('trialExpiry.noneExpired')}</p>
          ) : (
            <ul className="lm-location-list">
              {trialExpiry.expired.map((row) => (
                <li key={row.stockItemId} className="lm-location-row">
                  <span className="lm-location-row__name" dir="auto">
                    {row.locationName}
                  </span>
                  {/* A lot code is Latin whatever the page direction (`LOC-005`). */}
                  <span className="lm-location-row__meta" dir="ltr">
                    {row.lot === '' ? row.locationCode : row.lot}
                  </span>
                  <span className="lm-location-row__meta">
                    {t('trialExpiry.expiredOn', {
                      day: formatDay(row.expiryDate, timezone, locale),
                      quantity: row.quantity,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {expiryWindow === 0 ? null : (
            <>
              <h3 className="lm-field__label">
                {t('trialExpiry.soonHeading', { days: expiryWindow })}
              </h3>
              {trialExpiry.expiringSoon.length === 0 ? (
                <p className="lm-hint">{t('trialExpiry.noneSoon')}</p>
              ) : (
                <ul className="lm-location-list">
                  {trialExpiry.expiringSoon.map((row) => (
                    <li key={row.stockItemId} className="lm-location-row">
                      <span className="lm-location-row__name" dir="auto">
                        {row.locationName}
                      </span>
                      <span className="lm-location-row__meta" dir="ltr">
                        {row.lot === '' ? row.locationCode : row.lot}
                      </span>
                      <span className="lm-location-row__meta">
                        {t('trialExpiry.expiresOn', {
                          day: formatDay(row.expiryDate, timezone, locale),
                          quantity: row.quantity,
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {trialExpiry.undatedItems === 0 ? null : (
            // Counted, not dropped. A trial frame has no expiry and belongs in a number rather than
            // in a silence that implies the drawer holds only what is listed.
            <p className="lm-hint">
              {t('trialExpiry.undated', { count: trialExpiry.undatedItems })}
            </p>
          )}
        </section>
      )}
    </section>
  )
}
